// PautaCatalogo — /admin/comunicacion/pauta (06-ago-2026)
// Elegí qué productos empujar en los anuncios de catálogo de Meta. El agente
// prepara, vos publicás. Cada "conjunto" = subset de SKUs; se guarda en
// meta_ad_conjuntos y tiene su propia URL de feed (?conjunto=slug) para usar
// en Meta Ads Manager. Modelo "prepara, publicás vos": acá NO se gasta plata.
//
// Fuente productos: RPC tienda_get_productos() (mismo set que la tienda/feed).
import { useEffect, useMemo, useState } from 'react'
import AdminLayout from '../../components/AdminLayout'
import { supabase } from '../../lib/supabase'

const SUPA_URL = import.meta.env.VITE_SUPABASE_URL
const FEED_BASE = `${SUPA_URL}/functions/v1/feed-catalogo-meta`

const CAT_LABEL = {
  lavarropa: 'Para la ropa', cocina: 'Cocina', bano: 'Baño', papel: 'Papel', higiene: 'Higiene',
  manos: 'Manos', desinfectantes: 'Desinfectantes', bolsas: 'Bolsas', accesorios: 'Accesorios',
  otros: 'Otros', multiuso: 'Multiuso', pisos: 'Pisos', vidrios: 'Vidrios', lavandinas: 'Lavandinas',
  panuelos: 'Pañuelos', cuidado_personal: 'Cuidado personal', ambientadores: 'Ambientadores',
}
const catLabel = (c) => CAT_LABEL[c] || c || 'Otros'
const slugify = (s) => (s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
  .replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')

export default function PautaCatalogo() {
  const [productos, setProductos] = useState([])
  const [conjuntos, setConjuntos] = useState([])
  const [sel, setSel] = useState(() => new Set())
  const [cat, setCat] = useState('todos')
  const [nombre, setNombre] = useState('')
  const [loading, setLoading] = useState(true)
  const [guardando, setGuardando] = useState(false)
  const [msg, setMsg] = useState(null)
  const [copiado, setCopiado] = useState(null)

  useEffect(() => { cargar() }, [])

  async function cargar() {
    setLoading(true)
    try {
      const [{ data: prods }, { data: conj }] = await Promise.all([
        supabase.rpc('tienda_get_productos'),
        supabase.from('meta_ad_conjuntos').select('*').eq('activo', true).order('created_at', { ascending: false }),
      ])
      const list = (prods || []).map(p => ({
        sku: p.sku, nombre: p.nombre, categoria: p.categoria,
        precio: Number(p.precio_publico) || 0, img: p.imagen_url,
        agotado: Number(p.stock ?? 0) <= 0,
      })).sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'))
      setProductos(list)
      setConjuntos(conj || [])
    } catch (e) {
      setMsg({ t: 'err', x: 'No pude cargar: ' + e.message })
    } finally {
      setLoading(false)
    }
  }

  const cats = useMemo(() => {
    const c = {}
    for (const p of productos) c[p.categoria] = (c[p.categoria] || 0) + 1
    const arr = Object.keys(c).sort((a, b) => c[b] - c[a]).map(k => ({ k, label: catLabel(k), n: c[k] }))
    return [{ k: 'todos', label: 'Todos', n: productos.length }, ...arr]
  }, [productos])

  const visibles = useMemo(
    () => cat === 'todos' ? productos : productos.filter(p => p.categoria === cat),
    [productos, cat],
  )

  function toggle(sku) {
    setSel(prev => {
      const n = new Set(prev)
      n.has(sku) ? n.delete(sku) : n.add(sku)
      return n
    })
  }

  async function guardar() {
    if (!nombre.trim()) { setMsg({ t: 'err', x: 'Ponele un nombre al conjunto.' }); return }
    if (sel.size === 0) { setMsg({ t: 'err', x: 'Elegí al menos un producto.' }); return }
    setGuardando(true)
    const { data: { user } } = await supabase.auth.getUser()
    const base = slugify(nombre) || 'conjunto'
    let slug = base
    // Reintento con sufijo si el slug ya existe (unique constraint).
    let error = null, intento = 0
    do {
      const res = await supabase.from('meta_ad_conjuntos').insert({
        slug, nombre: nombre.trim(), skus: [...sel], created_by: user?.id,
      })
      error = res.error
      if (error && error.code === '23505') { intento += 1; slug = `${base}-${intento + 1}` }
      else break
    } while (intento < 20)
    setGuardando(false)
    if (error) { setMsg({ t: 'err', x: 'Error al guardar: ' + error.message }); return }
    setMsg({ t: 'ok', x: `✓ Conjunto "${nombre.trim()}" guardado con ${sel.size} producto${sel.size !== 1 ? 's' : ''}.` })
    setNombre('')
    await cargar()
  }

  function usar(conj) {
    setSel(new Set(conj.skus || []))
    setCat('todos')
    setMsg({ t: 'ok', x: `Cargué "${conj.nombre}" (${(conj.skus || []).length}). Editá la selección y guardá como nuevo, o usá su feed abajo.` })
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  async function borrar(conj) {
    if (!confirm(`¿Borrar el conjunto "${conj.nombre}"? (no borra productos, solo el conjunto)`)) return
    const { error } = await supabase.from('meta_ad_conjuntos').update({ activo: false }).eq('id', conj.id)
    if (error) { setMsg({ t: 'err', x: 'Error: ' + error.message }); return }
    await cargar()
  }

  function copiar(url, id) {
    navigator.clipboard?.writeText(url).then(() => {
      setCopiado(id); setTimeout(() => setCopiado(null), 1500)
    }).catch(() => {})
  }

  return (
    <AdminLayout title="Pauta de catálogo">
      <div className="pc-wrap">
        <header className="pc-head">
          <div>
            <div className="pc-eyebrow">COMUNICACIÓN · ANUNCIOS META</div>
            <h1 className="pc-title">Pauta de catálogo</h1>
            <p className="pc-sub">Elegí qué productos empujar en los anuncios. El agente los deja listos; publicás vos.
              No se gasta un peso hasta tu OK en Meta Ads Manager.</p>
          </div>
          <a href="https://business.facebook.com/adsmanager" target="_blank" rel="noopener" className="pc-btn pc-btn-ghost">Meta Ads Manager ↗</a>
        </header>

        {msg && <div className={`pc-msg pc-msg-${msg.t}`}>{msg.x}</div>}

        <div className="pc-grid">
          {/* IZQUIERDA: selector */}
          <div>
            <div className="pc-chips">
              {cats.map(c => (
                <button key={c.k} onClick={() => setCat(c.k)} className={`pc-chip ${cat === c.k ? 'on' : ''}`}>
                  {c.label} <span>{c.n}</span>
                </button>
              ))}
            </div>

            {loading ? (
              <div className="pc-loading">Cargando catálogo…</div>
            ) : (
              <div className="pc-prods">
                {visibles.map(p => {
                  const on = sel.has(p.sku)
                  return (
                    <button key={p.sku} onClick={() => toggle(p.sku)} className={`pc-cell ${on ? 'on' : ''}`} title={p.nombre}>
                      <span className={`pc-tick ${on ? '' : 'off'}`}>{on ? '✓' : ''}</span>
                      {p.img
                        ? <img src={p.img} alt="" loading="lazy" />
                        : <div className="pc-noimg">📦</div>}
                      <div className="pc-cap">
                        <b>{p.nombre}</b>
                        <span>${p.precio}{p.agotado ? ' · agotado' : ''}</span>
                      </div>
                    </button>
                  )
                })}
              </div>
            )}
          </div>

          {/* DERECHA: acción + conjuntos */}
          <aside className="pc-side">
            <div className="pc-selbox">
              <div className="pc-selnum">{sel.size}<span> elegido{sel.size !== 1 ? 's' : ''} de {productos.length}</span></div>
              {sel.size > 0 && <button className="pc-clear" onClick={() => setSel(new Set())}>limpiar</button>}
            </div>
            <input className="pc-input" placeholder='Nombre del conjunto (ej. "Lo más vendido")'
              value={nombre} onChange={e => setNombre(e.target.value)} />
            <button className="pc-btn pc-btn-primary" onClick={guardar} disabled={guardando}>
              {guardando ? 'Guardando…' : '💾 Guardar conjunto'}
            </button>
            <p className="pc-note">Guardás la selección con un nombre. Cada conjunto tiene su propia URL de feed para
              usar en Meta (creás un catálogo apuntando a esa URL, o un product set).</p>

            <div className="pc-conj-head">Conjuntos guardados</div>
            {conjuntos.length === 0 && <div className="pc-empty">Todavía no guardaste ninguno.</div>}
            {conjuntos.map(c => {
              const url = `${FEED_BASE}?conjunto=${c.slug}`
              return (
                <div key={c.id} className="pc-conj">
                  <div className="pc-conj-top">
                    <b>{c.nombre}</b>
                    <span>{(c.skus || []).length} prod.</span>
                  </div>
                  <div className="pc-conj-actions">
                    <button onClick={() => usar(c)} className="pc-mini">Usar</button>
                    <button onClick={() => copiar(url, c.id)} className="pc-mini">{copiado === c.id ? '¡copiado!' : 'Copiar feed'}</button>
                    <button onClick={() => borrar(c)} className="pc-mini pc-mini-x">🗑</button>
                  </div>
                  <a className="pc-conj-url" href={url} target="_blank" rel="noopener">{url}</a>
                </div>
              )
            })}
          </aside>
        </div>
      </div>
      <style>{CSS}</style>
    </AdminLayout>
  )
}

const CSS = `
.pc-wrap { padding: 16px; max-width: 1200px; margin: 0 auto; font-family: Inter, system-ui, sans-serif; color: #1A3461; }
.pc-head { display: flex; justify-content: space-between; align-items: flex-end; gap: 16px; margin-bottom: 16px; flex-wrap: wrap; }
.pc-eyebrow { font-size: 11px; font-weight: 800; color: #64748b; letter-spacing: .08em; }
.pc-title { font-size: 24px; font-weight: 900; margin: 4px 0 6px; }
.pc-sub { font-size: 13px; color: #64748b; max-width: 560px; line-height: 1.5; }
.pc-btn { padding: 9px 15px; border-radius: 9px; font-size: 13px; font-weight: 700; cursor: pointer; border: 0; font-family: inherit; text-decoration: none; display: inline-block; }
.pc-btn-ghost { background: #fff; border: 1px solid #E2E8F0; color: #1A3461; }
.pc-btn-primary { background: #00A8B5; color: #fff; width: 100%; box-shadow: 0 6px 16px rgba(0,168,181,.25); }
.pc-btn:disabled { opacity: .6; cursor: wait; }
.pc-msg { padding: 11px 14px; border-radius: 10px; margin-bottom: 14px; font-size: 13px; font-weight: 600; }
.pc-msg-ok { background: #ECFDF5; border: 1px solid #A7F3D0; color: #065F46; }
.pc-msg-err { background: #FEF2F2; border: 1px solid #FCA5A5; color: #991B1B; }
.pc-grid { display: grid; grid-template-columns: 1fr 320px; gap: 20px; align-items: start; }
@media (max-width: 860px) { .pc-grid { grid-template-columns: 1fr; } }
.pc-chips { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 12px; }
.pc-chip { font-size: 12px; font-weight: 700; padding: 6px 11px; border-radius: 999px; border: 1px solid #E2E8F0; background: #fff; color: #64748b; cursor: pointer; }
.pc-chip.on { background: #1A3461; color: #fff; border-color: #1A3461; }
.pc-chip span { opacity: .6; margin-left: 3px; }
.pc-loading, .pc-empty { padding: 24px; text-align: center; color: #94a3b8; font-size: 13px; }
.pc-prods { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; }
@media (max-width: 520px) { .pc-prods { grid-template-columns: repeat(3, 1fr); } }
.pc-cell { position: relative; background: #fff; border: 1.5px solid #E2E8F0; border-radius: 12px; overflow: hidden; cursor: pointer; padding: 0; font-family: inherit; text-align: left; transition: border-color .12s, box-shadow .12s; }
.pc-cell.on { border-color: #00A8B5; box-shadow: 0 0 0 3px rgba(0,168,181,.15); }
.pc-cell img, .pc-noimg { width: 100%; aspect-ratio: 1; object-fit: contain; background: #fff; padding: 10px 8px 2px; display: block; }
.pc-noimg { display: flex; align-items: center; justify-content: center; font-size: 26px; }
.pc-cell:not(.on) img { opacity: .72; }
.pc-tick { position: absolute; top: 7px; right: 7px; width: 20px; height: 20px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 900; color: #fff; background: #00A8B5; }
.pc-tick.off { background: #fff; border: 1.5px solid #E2E8F0; }
.pc-cap { padding: 5px 9px 9px; border-top: 1px solid #F1F5F9; }
.pc-cap b { font-size: 11px; color: #1A3461; display: block; line-height: 1.25; max-height: 2.5em; overflow: hidden; }
.pc-cap span { font-size: 11px; font-weight: 800; color: #00A8B5; }
.pc-side { position: sticky; top: 12px; background: #F8FAFC; border: 1px solid #E2E8F0; border-radius: 14px; padding: 14px; }
.pc-selbox { display: flex; align-items: baseline; justify-content: space-between; margin-bottom: 10px; }
.pc-selnum { font-size: 26px; font-weight: 900; color: #1A3461; }
.pc-selnum span { font-size: 12px; font-weight: 600; color: #64748b; margin-left: 5px; }
.pc-clear { background: none; border: 0; color: #00A8B5; font-weight: 700; font-size: 12px; cursor: pointer; }
.pc-input { width: 100%; border: 1.5px solid #DCE3EC; border-radius: 9px; padding: 10px 12px; font: inherit; font-size: 13px; margin-bottom: 8px; }
.pc-input:focus { outline: none; border-color: #00A8B5; box-shadow: 0 0 0 3px rgba(0,168,181,.12); }
.pc-note { font-size: 11.5px; color: #64748b; line-height: 1.5; margin: 10px 0 4px; }
.pc-conj-head { font-size: 11px; font-weight: 800; color: #64748b; letter-spacing: .05em; text-transform: uppercase; margin: 16px 0 8px; border-top: 1px solid #E2E8F0; padding-top: 14px; }
.pc-conj { background: #fff; border: 1px solid #E2E8F0; border-radius: 10px; padding: 9px 11px; margin-bottom: 8px; }
.pc-conj-top { display: flex; justify-content: space-between; align-items: baseline; }
.pc-conj-top b { font-size: 13px; color: #1A3461; }
.pc-conj-top span { font-size: 11px; color: #94a3b8; font-weight: 700; }
.pc-conj-actions { display: flex; gap: 6px; margin: 7px 0; }
.pc-mini { font-size: 11.5px; font-weight: 700; padding: 4px 9px; border-radius: 7px; border: 1px solid #E2E8F0; background: #fff; color: #1A3461; cursor: pointer; }
.pc-mini-x { color: #B91C1C; border-color: #F3C9D1; margin-left: auto; }
.pc-conj-url { font-size: 10.5px; color: #64748b; word-break: break-all; text-decoration: none; display: block; }
.pc-conj-url:hover { color: #00A8B5; }
`
